import { isServerOnlyPlatformImport, sanitizePlatformImportScript } from './parse.service'

export type ScriptClientImportPreflightOptions = {
  clientImportNames?: readonly string[]
  serverOnlyImportNames?: readonly string[]
}

/** Packages.* paths shimmed in browser En runtime (en-packages.ts). */
const BROWSER_SAFE_PACKAGES_PREFIXES = [
  'Packages.ru.esoft.fatclient.tools.Messages',
  'Packages.ru.esoft.fatclient.tools.ProgressBar',
  'Packages.ru.esoft.fatclient.tools.Dates',
  'Packages.ru.esoft.wf.client.constants.LocalProperties',
  'Packages.ru.esoft.tools.DateHelper',
  'Packages.ru.esoft.tools.FileHelper',
  'Packages.ru.esoft.Platform.util.tools.UniDataTools',
  'Packages.java.util.Date',
] as const

function stripBrowserSafePackagesReferences(script: string): string {
  let result = script
  for (const prefix of BROWSER_SAFE_PACKAGES_PREFIXES) {
    const escaped = prefix.replace(/\./g, '\\.')
    result = result.replace(new RegExp(escaped, 'g'), '')
  }
  return result
}

/** Action script (onClick) with Rhino/Java deps that En cannot satisfy in browser. */
export function isServerOnlyActionScript(sanitizedScript: string): boolean {
  if (!sanitizedScript?.trim()) {
    return false
  }
  if (/\bimportClass\s*\(/.test(sanitizedScript)) {
    return true
  }
  if (/\bimportPackage\s*\(/.test(sanitizedScript)) {
    return true
  }
  if (/\bstorage\./.test(sanitizedScript)) {
    return true
  }

  const withoutSafePackages = stripBrowserSafePackagesReferences(sanitizedScript)
  if (/\bPackages\./.test(withoutSafePackages)) {
    return true
  }

  const withoutJavaDate = withoutSafePackages.replace(/\bjava\.util\.Date\b/g, '')
  if (/\bjava\./.test(withoutJavaDate)) {
    return true
  }

  if (/\bnew\s+[A-Za-z_$][\w$.]*\s*\(\s*[^)]*\)\s*\{/.test(sanitizedScript)) {
    return true
  }

  return false
}

/** Strip Rhino importClass lines (parity with En.run). */
export function sanitizeActionScript(script: string): string {
  if (!script) return ''
  return script.replace(/importClass\s*\([^)]+\)\s*;?\s*/g, '').trim()
}

export function extractClientImportNamesFromScript(script: string): string[] {
  const names = new Set<string>()
  const patterns = [
    /\$\(\s*['"]([^'"]+)['"]\s*\)/g,
    /importer\.get\(\s*['"]([^'"]+)['"]\s*\)/g,
  ]

  for (const pattern of patterns) {
    for (const match of script.matchAll(pattern)) {
      const name = match[1]?.trim()
      if (name) {
        names.add(name)
      }
    }
  }

  return [...names]
}

/** True when script must run on middle due to server-only import deps or Rhino API in body. */
export function requiresMiddleForClientImportDeps(
  script: string,
  options: ScriptClientImportPreflightOptions = {},
): boolean {
  const sanitized = sanitizeActionScript(script)

  if (isServerOnlyActionScript(sanitized)) {
    return true
  }

  const clientSet = new Set(options.clientImportNames ?? [])
  const serverOnlySet = new Set(options.serverOnlyImportNames ?? [])

  for (const name of extractClientImportNamesFromScript(sanitized)) {
    if (serverOnlySet.has(name)) {
      return true
    }
    if (!clientSet.has(name)) {
      if (import.meta.env.DEV) {
        console.warn(
          `[preflight] unknown client import "${name}", routing onClick to middle`,
        )
      }
      return true
    }
  }

  return false
}

/** Browser-safe platform import after stripping importClass (for tests / diagnostics). */
export function isBrowserSafePlatformImport(rawCode: string): boolean {
  return !isServerOnlyPlatformImport(sanitizePlatformImportScript(rawCode))
}
